import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useMemo } from 'react';
import { z } from 'zod';

import { useGetContentQuery } from '@/api/api-hooks';
import { LinkItem } from '@/components/RepoHome/LinkItem';
import { RepoHome } from '@/components/RepoHome/RepoHome';
import { RepoHomeMainSidebar } from '@/components/RepoHome/RepoHomeMainSidebar';
import { LeftSidebarPortal } from '@/components/portals/LeftSidebarPortal';
import { CONTENT, getAutodocsDescription, getAutodocsTitle } from '@/constants/contents';
import { useCodebase } from '@/contexts/CodebaseContext';
import { SourceOverviewProvider, useSourceOverview } from '@/contexts/SourceOverviewContext';
import { useCodebaseRoute } from '@/hooks/useCodebaseRoute';
import { useDefaultBranchLatestVersion } from '@/hooks/useDefaultBranchLatestVersion';
import { buildVersionedUrl } from '@/utils/codebaseUrl';
import { searchParam } from '@/utils/router';

const treeSearchSchema = z.object({
  branchId: searchParam().optional(),
  versionId: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/cb/$codebaseId/overview/$')({
  component: CodebasePage,
  validateSearch: zodValidator(treeSearchSchema),
});

function CodebasePage() {
  return (
    <SourceOverviewProvider>
      <OverviewContent />
    </SourceOverviewProvider>
  );
}

function OverviewContent() {
  const { codebaseId, organizationName } = useCodebaseRoute();
  const {
    description,
    isContentLoading,
    wasOverviewProcessed,
    topAudience,
    topClassification,
    topDomain,
    versionControl,
    languagesArrWithPercentages,
    tags,
    sourceType,
  } = useSourceOverview();
  const { codebase, codebaseName, isPrimaryAssetLoading } = useCodebase();
  const {
    defaultBranchId,
    defaultBranchLatestVersion,
    hasBranches,
    isLoading: isDbvLoading,
    defaultBranchLatestVersionId,
    versionId,
  } = useDefaultBranchLatestVersion();

  const { data: autoDocsData } = useGetContentQuery(
    {
      version_node_id: defaultBranchLatestVersionId ?? '',
      include_content: false,
      content_kind: [
        CONTENT.DEEP_CONTEXT_CHANGELOG,
        CONTENT.DEEP_CONTEXT_ARCHITECTURE,
        CONTENT.DEEP_CONTEXT_LLM_ONBOARDING,
      ],
      limit: 20,
    },
    { enabled: !!defaultBranchLatestVersionId, refetchOnWindowFocus: false }
  );

  const generateDocSourceVersionNodeIds = useMemo(() => {
    return defaultBranchLatestVersionId ? [defaultBranchLatestVersionId] : [];
  }, [defaultBranchLatestVersionId]);

  const autoDocsFormattedData: LinkItem[] | undefined = useMemo(() => {
    return (
      autoDocsData?.results?.map((record) => {
        const displayTitle = getAutodocsTitle(record.content_kind);
        const description = getAutodocsDescription(
          displayTitle,
          record.content || record.misc_metadata?.single_sentence_description
        );

        const autodocsUrl = buildVersionedUrl(`/${organizationName}/cb/${codebaseId}/autodocs/${record.id}`, {
          branchId: hasBranches ? defaultBranchId : undefined,
          versionId,
        });
        return {
          name: displayTitle || 'Untitled Autodoc',
          url: autodocsUrl,
          description: description,
        };
      }) || undefined
    );
  }, [autoDocsData, organizationName, codebaseId, hasBranches, defaultBranchId, versionId]);

  return (
    <>
      <LeftSidebarPortal.Mount>
        <RepoHomeMainSidebar
          linesOfCode={defaultBranchLatestVersion?.root_version_node?.misc_metadata?.analyzable_sloc}
          totalFiles={defaultBranchLatestVersion?.root_version_node?.misc_metadata?.total_files}
          driverIgnoredFiles={defaultBranchLatestVersion?.root_version_node?.misc_metadata?.driver_ignored_files}
          audience={topAudience}
          classification={topClassification}
          domain={topDomain}
          versionControl={versionControl}
          languages={languagesArrWithPercentages}
          tags={tags}
          displayName={codebase?.display_name}
          primaryAssetId={codebase?.id}
          sourceType={sourceType ?? ''}
        />
      </LeftSidebarPortal.Mount>
      <RepoHome
        name={codebase?.display_name}
        description={description}
        codebaseId={codebaseId}
        versionId={versionId}
        to="/$organizationName/cb/$codebaseId/tree/$"
        params={{
          organizationName: organizationName,
          codebaseId: codebaseId,
          _splat: codebaseName ?? '',
        }}
        search={
          (hasBranches && defaultBranchId) || versionId
            ? {
                ...(hasBranches && defaultBranchId ? { branchId: defaultBranchId } : {}),
                ...(versionId ? { versionId } : {}),
              }
            : undefined
        }
        rootNodeId={defaultBranchLatestVersionId}
        autodocs={autoDocsFormattedData}
        isLoading={isContentLoading || isPrimaryAssetLoading || isDbvLoading}
        mostRecentStatus={codebase?.most_recent_version?.status}
        wasOverviewProcessed={wasOverviewProcessed}
        sourceVersionNodeIds={generateDocSourceVersionNodeIds}
      />
    </>
  );
}
